import { useRouterState } from '@tanstack/react-router';
import { ChevronDown, KeyRound, LogOut, Menu } from 'lucide-react';
import type { JSX, RefObject } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { ChangePasswordDialog } from '@/features/auth/change-password-dialog.js';
import { useCurrentUser, useLogout } from '@/features/auth/use-auth.js';
import { NAV_ENTRIES } from './nav-config.js';

export interface TopBarProps {
  onMenuClick: () => void;
  menuButtonRef?: RefObject<HTMLButtonElement | null>;
}

function initialsOf(name: string | undefined): string {
  if (!name) {
    return '?';
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    parts.length > 1
      ? `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`
      : (parts[0] ?? '').slice(0, 2);
  return letters.toUpperCase() || '?';
}

/** Section courante, rappelée dans la barre supérieure quand la barre latérale est repliée (< 1024 px). */
function useCurrentSectionLabel(): string | undefined {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const match = [...NAV_ENTRIES]
    .filter((entry) => (entry.path === '/' ? pathname === '/' : pathname.startsWith(entry.path)))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return match?.label;
}

/**
 * Barre supérieure de 48 px (`02` §0) : bouton de menu et section courante
 * sous 1024px, menu de compte (changer le mot de passe, déconnexion). Plus de
 * sélecteur de boutique : chaque liste porte son propre filtre « Origine ».
 */
export function TopBar({ onMenuClick, menuButtonRef }: TopBarProps): JSX.Element {
  const { data: user } = useCurrentUser();
  const { logout, isPending: loggingOut } = useLogout();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const sectionLabel = useCurrentSectionLabel();

  return (
    <header className="flex h-12 items-center gap-2 border-b border-border bg-background px-2 md:px-4 lg:px-8">
      <Button
        ref={menuButtonRef}
        type="button"
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenuClick}
        aria-label="Ouvrir la navigation"
      >
        <Menu aria-hidden="true" />
      </Button>
      {sectionLabel ? (
        <span className="truncate text-sm font-medium text-foreground lg:hidden">
          {sectionLabel}
        </span>
      ) : null}

      <div className="flex-1" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className="h-9 gap-2 px-1.5 sm:pr-2.5">
            <span
              aria-hidden="true"
              className="flex size-6 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground"
            >
              {initialsOf(user?.name)}
            </span>
            <span className="hidden max-w-40 truncate text-sm sm:inline">
              {user?.name ?? 'Compte'}
            </span>
            <span className="sr-only sm:hidden">{user?.name ?? 'Compte'}</span>
            <ChevronDown className="hidden sm:block" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
            <span className="truncate text-sm font-medium text-foreground">{user?.name}</span>
            <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              setChangePasswordOpen(true);
            }}
          >
            <KeyRound aria-hidden="true" />
            Changer le mot de passe
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={loggingOut}
            onSelect={() => {
              logout();
            }}
          >
            <LogOut aria-hidden="true" />
            Se déconnecter
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </header>
  );
}
